import api from './api';
import { API_ROUTES } from '@/lib/constants';
import { createCrudService } from './crud.factory';
import type { ApiResponse, PaginatedResponse } from '@/types/table';
import type {
  BackdateImpact,
  Employee,
  PayComponent,
  MyEmployee,
  PayrollRun,
  Payslip,
  PayslipPeriodHistory,
  PayslipPreview,
  RunEligibility,
  SalaryBreakdown,
  SalaryStructure,
  StructureLineInput,
} from '@/types/payroll';

export const employeeService = {
  ...createCrudService<Employee>(API_ROUTES.EMPLOYEES),

  getNextCode: async (): Promise<ApiResponse<{ nextCode: string }>> => {
    const response = await api.get(`${API_ROUTES.EMPLOYEES}/next-code`);
    return response.data;
  },

  getSalaryStructures: async (employeeId: string): Promise<ApiResponse<SalaryStructure[]>> => {
    const response = await api.get(`${API_ROUTES.EMPLOYEES}/${employeeId}/salary-structures`);
    return response.data;
  },

  /** Issued payslips a proposed effective date would reach back over. */
  backdateImpact: async (employeeId: string, effectiveFrom: string): Promise<ApiResponse<BackdateImpact>> => {
    const response = await api.get(
      `${API_ROUTES.EMPLOYEES}/${employeeId}/salary-structures/backdate-impact`,
      { params: { effectiveFrom } },
    );
    return response.data;
  },

  createSalaryStructure: async (
    employeeId: string,
    data: {
      effectiveFrom: string;
      ctcAnnual: number;
      currency?: string;
      notes?: string;
      revisionType?: string;
      reason?: string;
      lines: StructureLineInput[];
    },
  ): Promise<ApiResponse<SalaryStructure>> => {
    const response = await api.post(`${API_ROUTES.EMPLOYEES}/${employeeId}/salary-structures`, data);
    return response.data;
  },
};

export const payComponentService = {
  getAll: async (includeInactive = false): Promise<ApiResponse<PayComponent[]>> => {
    const response = await api.get(API_ROUTES.PAY_COMPONENTS, {
      // Only send the flag when it is on — the API rejects unknown/empty params.
      params: includeInactive ? { includeInactive: 'true' } : undefined,
    });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<PayComponent>> => {
    const response = await api.get(`${API_ROUTES.PAY_COMPONENTS}/${id}`);
    return response.data;
  },

  create: async (data: Record<string, unknown>): Promise<ApiResponse<PayComponent>> => {
    const response = await api.post(API_ROUTES.PAY_COMPONENTS, data);
    return response.data;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<ApiResponse<PayComponent>> => {
    const response = await api.patch(`${API_ROUTES.PAY_COMPONENTS}/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_ROUTES.PAY_COMPONENTS}/${id}`);
  },

  /**
   * Stateless breakdown for the live salary editor — same maths as a save,
   * but nothing is persisted, so it is safe to call on every change.
   */
  preview: async (data: {
    ctcAnnual: number;
    lines: StructureLineInput[];
  }): Promise<ApiResponse<SalaryBreakdown>> => {
    const response = await api.post(`${API_ROUTES.PAY_COMPONENTS}/preview`, data);
    return response.data;
  },
};

export const salaryStructureService = {
  getById: async (id: string): Promise<ApiResponse<SalaryStructure>> => {
    const response = await api.get(`${API_ROUTES.SALARY_STRUCTURES}/${id}`);
    return response.data;
  },

  getBreakdown: async (id: string): Promise<ApiResponse<SalaryBreakdown>> => {
    const response = await api.get(`${API_ROUTES.SALARY_STRUCTURES}/${id}/breakdown`);
    return response.data;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<ApiResponse<SalaryStructure>> => {
    const response = await api.patch(`${API_ROUTES.SALARY_STRUCTURES}/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_ROUTES.SALARY_STRUCTURES}/${id}`);
  },
};

export type { PaginatedResponse };

export const payslipService = {
  ...createCrudService<Payslip>(API_ROUTES.PAYSLIPS),

  /** Computes a payslip without saving — drives the generate screen. */
  preview: async (data: {
    employeeId: string;
    periodMonth: number;
    periodYear: number;
    payDate?: string;
    paidDays?: number;
    totalDays?: number;
  }): Promise<ApiResponse<PayslipPreview>> => {
    const response = await api.post(`${API_ROUTES.PAYSLIPS}/preview`, data);
    return response.data;
  },

  generate: async (data: Record<string, unknown>): Promise<ApiResponse<Payslip>> => {
    const response = await api.post(API_ROUTES.PAYSLIPS, data);
    return response.data;
  },

  publish: async (id: string): Promise<ApiResponse<Payslip>> => {
    const response = await api.post(`${API_ROUTES.PAYSLIPS}/${id}/publish`);
    return response.data;
  },

  markPaid: async (id: string): Promise<ApiResponse<Payslip>> => {
    const response = await api.post(`${API_ROUTES.PAYSLIPS}/${id}/mark-paid`);
    return response.data;
  },

  cancel: async (id: string): Promise<ApiResponse<Payslip>> => {
    const response = await api.post(`${API_ROUTES.PAYSLIPS}/${id}/cancel`);
    return response.data;
  },

  /**
   * Issues a corrected payslip in place of an issued one. Returns the draft
   * replacement — the original stays on record as SUPERSEDED.
   */
  supersede: async (id: string, data: Record<string, unknown> = {}): Promise<ApiResponse<Payslip>> => {
    const response = await api.post(`${API_ROUTES.PAYSLIPS}/${id}/supersede`, data);
    return response.data;
  },

  /** Descriptive-only fix, allowed even after publishing. */
  updateDetails: async (id: string, data: Record<string, unknown>): Promise<ApiResponse<Payslip>> => {
    const response = await api.patch(`${API_ROUTES.PAYSLIPS}/${id}/details`, data);
    return response.data;
  },

  /** Every payslip ever issued for this one's employee-month. */
  periodHistory: async (id: string): Promise<ApiResponse<PayslipPeriodHistory>> => {
    const response = await api.get(`${API_ROUTES.PAYSLIPS}/${id}/history`);
    return response.data;
  },

  downloadPdf: async (id: string): Promise<Blob> => {
    const response = await api.get(`${API_ROUTES.PAYSLIPS}/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },
};

export const payrollRunService = {
  ...createCrudService<PayrollRun>(API_ROUTES.PAYROLL_RUNS),

  /** Who would be included in a run for a period, and who is skipped. */
  eligible: async (periodYear: number, periodMonth: number): Promise<ApiResponse<RunEligibility>> => {
    const response = await api.get(`${API_ROUTES.PAYROLL_RUNS}/eligible`, {
      params: { periodYear, periodMonth },
    });
    return response.data;
  },

  updatePayslip: async (
    runId: string, payslipId: string,
    data: { paidDays?: number; totalDays?: number; notes?: string },
  ): Promise<ApiResponse<Payslip>> => {
    const response = await api.patch(`${API_ROUTES.PAYROLL_RUNS}/${runId}/payslips/${payslipId}`, data);
    return response.data;
  },

  addAdjustment: async (
    runId: string, payslipId: string,
    data: { label: string; type: string; amount: number; notes?: string },
  ): Promise<ApiResponse<Payslip>> => {
    const response = await api.post(`${API_ROUTES.PAYROLL_RUNS}/${runId}/payslips/${payslipId}/adjustments`, data);
    return response.data;
  },

  removeAdjustment: async (runId: string, payslipId: string, adjustmentId: string): Promise<ApiResponse<Payslip>> => {
    const response = await api.delete(
      `${API_ROUTES.PAYROLL_RUNS}/${runId}/payslips/${payslipId}/adjustments/${adjustmentId}`,
    );
    return response.data;
  },

  excludePayslip: async (runId: string, payslipId: string): Promise<ApiResponse<PayrollRun>> => {
    const response = await api.delete(`${API_ROUTES.PAYROLL_RUNS}/${runId}/payslips/${payslipId}`);
    return response.data;
  },

  submit: async (id: string): Promise<ApiResponse<PayrollRun>> => {
    const response = await api.post(`${API_ROUTES.PAYROLL_RUNS}/${id}/submit`);
    return response.data;
  },

  reopen: async (id: string): Promise<ApiResponse<PayrollRun>> => {
    const response = await api.post(`${API_ROUTES.PAYROLL_RUNS}/${id}/reopen`);
    return response.data;
  },

  approve: async (id: string, data: { accountId: string; categoryId?: string }): Promise<ApiResponse<PayrollRun>> => {
    const response = await api.post(`${API_ROUTES.PAYROLL_RUNS}/${id}/approve`, data);
    return response.data;
  },

  markPaid: async (id: string): Promise<ApiResponse<PayrollRun>> => {
    const response = await api.post(`${API_ROUTES.PAYROLL_RUNS}/${id}/mark-paid`);
    return response.data;
  },

  cancel: async (id: string): Promise<ApiResponse<PayrollRun>> => {
    const response = await api.post(`${API_ROUTES.PAYROLL_RUNS}/${id}/cancel`);
    return response.data;
  },
};

/**
 * The signed-in employee's own payslips. Every route is scoped server-side to
 * the employee behind the JWT — no id is ever sent from the client.
 */
export const selfServiceService = {
  myEmployee: async (): Promise<ApiResponse<MyEmployee>> => {
    const response = await api.get(`${API_ROUTES.ME}/employee`);
    return response.data;
  },

  myPayslips: async (params?: object): Promise<PaginatedResponse<Payslip>> => {
    const response = await api.get(`${API_ROUTES.ME}/payslips`, { params });
    return response.data;
  },

  myPayslip: async (id: string): Promise<ApiResponse<Payslip>> => {
    const response = await api.get(`${API_ROUTES.ME}/payslips/${id}`);
    return response.data;
  },

  myPayslipPdf: async (id: string): Promise<Blob> => {
    const response = await api.get(`${API_ROUTES.ME}/payslips/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },
};

/** Admin-side email actions. */
export const payslipEmailService = {
  emailOne: async (payslipId: string): Promise<ApiResponse<{ queued: unknown[]; skipped: unknown[] }>> => {
    const response = await api.post(`${API_ROUTES.PAYSLIPS}/${payslipId}/email`);
    return response.data;
  },
  emailRun: async (runId: string): Promise<ApiResponse<{ queued: unknown[]; skipped: Array<{ payslipNumber: string; reason: string }> }>> => {
    const response = await api.post(`${API_ROUTES.PAYROLL_RUNS}/${runId}/email-all`);
    return response.data;
  },
};
